import * as React from "react"
import { useEffectOnce } from "@digits-shared/hooks/useEffectOnce"
import useSession from "@digits-shared/hooks/useSession"
import { useChromeStructure } from "src/frontend/components/OS/Chrome/Desktop/chromeStructure"
import {
  modalDismissalsSelector,
  modalNameSelector,
} from "src/frontend/components/Shared/ManagedModals/state/selectors"
import { makeModalStorageId } from "src/frontend/components/Shared/ManagedModals/state/storageIds"
import {
  useModalManagerDispatch,
  useModalManagerStore,
} from "src/frontend/components/Shared/ManagedModals/state/store"
import {
  type Dismissal,
  type ManagedModalName,
} from "src/frontend/components/Shared/ManagedModals/state/types"
import { getLocalPreference } from "src/frontend/components/Shared/ManagedModals/storage"
import type FrontendSession from "src/frontend/session"

/**
 * Returns true if the modal should be shown, optionally deferring to details views
 */
export function useShouldShowModal(modalName: ManagedModalName, hideOnActiveDetailsView?: boolean) {
  const currentOpenModal = useModalManagerStore(modalNameSelector)
  const isModalOpen = modalName === currentOpenModal

  const { hasActiveDetailsView } = useChromeStructure()
  const hideForDetailsView = hideOnActiveDetailsView && hasActiveDetailsView

  return isModalOpen && !hideForDetailsView
}

/**
 * Returns a memoized callback to open `modalName`
 */
export function useShowModal(modalName: ManagedModalName) {
  const dispatch = useModalManagerDispatch()
  return React.useCallback(
    () => dispatch({ type: "OpenModal", name: modalName }),
    [dispatch, modalName]
  )
}

/**
 * Sets `modalName` as the active modal if it was not previously dismissed.
 * Runs once.
 */
export function useShowModalIfNotDismissed(modalName: ManagedModalName) {
  const wasDismissed = useWasModalDismissed(modalName)
  const showModal = useShowModal(modalName)
  useEffectOnce(() => {
    if (!wasDismissed) {
      showModal()
    }
  })
}

/**
 * Returns a memoized callback to hide `modalName`
 */
export function useHideModal(modalName: ManagedModalName, dismissal?: Dismissal) {
  const dispatch = useModalManagerDispatch()
  const { currentLegalEntityId } = useSession<FrontendSession>()
  return React.useCallback(
    () =>
      dispatch({
        type: "CloseModal",
        name: modalName,
        dismissal,
        legalEntityId: dismissal === "Persistent" ? currentLegalEntityId : undefined,
      }),
    [dispatch, modalName, currentLegalEntityId, dismissal]
  )
}

/**
 * Returns true if the modal was dismissed in the current session
 * or was previously persistently dismissed.
 */
export function useWasModalDismissed(modalName: ManagedModalName) {
  const { currentLegalEntityId } = useSession<FrontendSession>()
  const dismissals = useModalManagerStore(modalDismissalsSelector)
  const persistentlyDismissedAt = React.useMemo(
    () => getLocalPreference(makeModalStorageId(modalName), currentLegalEntityId),
    [modalName, currentLegalEntityId]
  )
  const wasDismissed = React.useMemo(() => dismissals.has(modalName), [modalName, dismissals])

  return Boolean(persistentlyDismissedAt || wasDismissed)
}

interface ManagedModalOptions {
  /**
   * Default: true. Override to prevent a modal from being shown.
   */
  canShow?: boolean
}

interface ManagedModalHookResult {
  /**
   * By default, modal dismissals are persistent for an LE.
   */
  dismissModal: () => void
  /**
   * True if this modal is the Managed Modal selected to be visible AND if it
   * was not previously dismissed.
   */
  isModalVisible: boolean
}

/**
 * Control the visibility of a managed modal. Managed modals prevent other
 * modals and snackbars from being shown while they are open.
 * Modal is shown immediately if not previously dismissed.
 *
 * @param modalName Name of the modal to display
 * @param options Options to control the modal behavior
 */
export function useManagedModal(
  modalName: ManagedModalName,
  options?: ManagedModalOptions
): ManagedModalHookResult {
  const { canShow = true } = options ?? {}
  const wasDismissed = useWasModalDismissed(modalName)
  const showModal = useShowModal(modalName)
  const dismissModal = useHideModal(modalName, "Persistent")
  const shouldShowModal = useShouldShowModal(modalName)

  React.useEffect(() => {
    if (!wasDismissed && canShow) {
      showModal()
    }
  }, [modalName, canShow, wasDismissed, showModal])

  return React.useMemo(
    () => ({
      dismissModal,
      isModalVisible: shouldShowModal && !wasDismissed,
    }),
    [dismissModal, wasDismissed, shouldShowModal]
  )
}
